import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { compareAsc, isEqual, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { type AmPm } from "./AmPmToggle";

export function parseTimeString(
  timeString: string
): { hours: number; minutes: number } | undefined {
  const trimmed = timeString.trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!match) {
    return undefined;
  }

  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);

  if (hours < 0 || hours > 12 || minutes < 0 || minutes > 59) {
    return undefined;
  }

  return { hours, minutes };
}

const MAX_TIME_INPUT_LENGTH = 5;

/**
 * Treats the input as a stream of digits and formats it into h:mm or hh:mm.
 * All non-digit characters (including colons) are stripped; the colon is
 * inserted automatically based on the digit count:
 *
 *   1 digit         → as-is (e.g. "1")
 *   2 digits ≤ 12   → as-is (e.g. "12")
 *   2 digits ≥ 13   → d:d   (e.g. "13" → "1:3")
 *   3 digits, last two ≤ 59 → d:dd (e.g. "115" → "1:15")
 *   3 digits, last two ≥ 60 → dd:d (e.g. "127" → "12:7")
 *   4 digits        → dd:dd (e.g. "1234" → "12:34")
 *
 * The caret is positioned by counting how many digits precede it in the raw
 * input and finding that same digit offset in the formatted output. This keeps
 * the cursor stable across reformats instead of jumping to the end.
 */
export function sanitizeTimeInput(
  raw: string,
  rawCaret?: number
): { value: string; caret: number } {
  const digits = raw.replace(/\D/g, "").slice(0, 4);

  if (digits.length === 0) {
    return { value: "", caret: 0 };
  }

  let formatted: string;

  switch (digits.length) {
    case 1: {
      formatted = digits;
      break;
    }

    case 2: {
      const twoDigitValue = Number.parseInt(digits, 10);
      formatted = twoDigitValue >= 13 ? `${digits[0]}:${digits[1]}` : digits;
      break;
    }

    case 3: {
      const lastTwo = Number.parseInt(digits.slice(1), 10);
      formatted =
        lastTwo <= 59 ? `${digits[0]}:${digits.slice(1)}` : `${digits.slice(0, 2)}:${digits[2]}`;
      break;
    }

    default: {
      formatted = `${digits.slice(0, 2)}:${digits.slice(2)}`.slice(0, MAX_TIME_INPUT_LENGTH);
      break;
    }
  }

  const caret = mapCaretToFormatted(formatted, raw, rawCaret ?? raw.length);
  return { value: formatted, caret };
}

function mapCaretToFormatted(formatted: string, raw: string, rawCaret: number): number {
  let digitsBefore = 0;
  for (const char of raw.slice(0, rawCaret)) {
    if (/\d/.test(char)) {
      digitsBefore++;
    }
  }

  if (digitsBefore <= 0) {
    return 0;
  }

  let count = 0;
  for (const [index, char] of [...formatted].entries()) {
    if (/\d/.test(char)) {
      count++;
    }

    if (count === digitsBefore) {
      return index + 1;
    }
  }

  return formatted.length;
}

export function formatTimeString(hours: number, minutes: number): string {
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
}

export function to12Hour(hours24: number): { hours12: number; amPm: AmPm } {
  if (hours24 === 0) {
    return { hours12: 12, amPm: "am" };
  }
  if (hours24 === 12) {
    return { hours12: 12, amPm: "pm" };
  }
  if (hours24 > 12) {
    return { hours12: hours24 - 12, amPm: "pm" };
  }
  return { hours12: hours24, amPm: "am" };
}

export function to24Hour(hours12: number, amPm: AmPm): number {
  if (amPm === "am") {
    return hours12 === 12 ? 0 : hours12;
  }
  return hours12 === 12 ? 12 : hours12 + 12;
}

interface GetBorderColorParams {
  hasError: boolean;
  isFocused: boolean;
  theme: Theme;
  isHover?: boolean;
}

export function getBorderColor(params: GetBorderColorParams): string | undefined {
  const { hasError, isFocused, theme, isHover = false } = params;

  if (hasError) {
    return theme.palette.error.main;
  }

  if (isFocused) {
    return theme.palette.primary.main;
  }

  return isHover ? theme.palette.border?.strong : theme.palette.border?.base;
}

interface DayOptions {
  minDay: Date;
  maxDay: Date;
}

export function getDayOptions(
  startDate?: Date,
  endDate?: Date,
  timezone?: string
): DayOptions | undefined {
  if (!isDefined(startDate) || !isDefined(endDate)) {
    return undefined;
  }

  const zonedStart = isDefined(timezone) ? utcToZonedTime(startDate, timezone) : startDate;
  const zonedEnd = isDefined(timezone) ? utcToZonedTime(endDate, timezone) : endDate;
  const zonedStartDay = startOfDay(zonedStart);
  const zonedEndDay = startOfDay(zonedEnd);

  if (isEqual(zonedStartDay, zonedEndDay)) {
    return undefined;
  }

  const isStartDayBeforeOrSame = compareAsc(zonedStartDay, zonedEndDay) <= 0;
  const minDay = isStartDayBeforeOrSame ? zonedStartDay : zonedEndDay;
  const maxDay = isStartDayBeforeOrSame ? zonedEndDay : zonedStartDay;

  return {
    minDay: isDefined(timezone) ? zonedTimeToUtc(minDay, timezone) : minDay,
    maxDay: isDefined(timezone) ? zonedTimeToUtc(maxDay, timezone) : maxDay,
  };
}

export function getSelectedDay(value: Date, timezone?: string): Date {
  const zonedValue = isDefined(timezone) ? utcToZonedTime(value, timezone) : value;
  const zonedSelectedDay = startOfDay(zonedValue);
  return isDefined(timezone) ? zonedTimeToUtc(zonedSelectedDay, timezone) : zonedSelectedDay;
}

export function updateDateInTimezone(value: Date, newDate: Date, timezone?: string): Date {
  const zonedValue = isDefined(timezone) ? utcToZonedTime(value, timezone) : value;
  const zonedDate = isDefined(timezone) ? utcToZonedTime(newDate, timezone) : newDate;
  const updatedDateTime = new Date(zonedDate);
  updatedDateTime.setHours(zonedValue.getHours(), zonedValue.getMinutes(), 0, 0);
  return isDefined(timezone) ? zonedTimeToUtc(updatedDateTime, timezone) : updatedDateTime;
}
